<template>
  <div class="mt-4">
    <p v-if="!isUserLoaded" class="text-sm text-gray-500">Loading…</p>

    <!-- Already purged: never render a live form. -->
    <div v-else-if="user.data_purged_at">
      <p class="text-sm font-medium text-gray-700">
        This account was erased on {{ formattedDate(user.data_purged_at) }}.
      </p>
      <p class="mt-2 text-sm text-gray-500">
        Personal data is gone and cannot be restored. Billing history, job
        counts and the audit record are kept on purpose.
      </p>
      <button type="button" class="button-cancel m-0 mt-5" @click="openRecord">
        View erasure record
      </button>
    </div>

    <!-- An unfinished request exists. The endpoint rejects a second one, and
         after a partial run the email is already a tombstone so there is no
         address left to retype - resuming goes through `retry`. -->
    <div v-else-if="outstanding">
      <p v-if="latestStatus === 'PARTIAL'" class="text-sm text-red-600">
        The last purge finished with failed steps, so the account is not marked
        erased. Access is still cut. Retry to resume it.
      </p>
      <p v-else class="text-sm text-yellow-700">
        A purge is {{ latestStatus === "IN_PROGRESS" ? "running" : "queued" }}
        for this account (requested
        {{ formattedDate(latestRecord.requested_at) }}). It runs in the
        background and can take a few minutes.
      </p>
      <p class="mt-2 text-sm text-gray-500">
        Access to the account was cut when the request was filed.
      </p>

      <div class="flex items-center">
        <!-- Only for a run that is actually stuck or finished badly. A PENDING
             row, or one that started moments ago, is doing exactly what it
             should; offering Retry there invites an admin to re-dispatch work
             that is already queued (the worker's claim makes that a no-op, but
             the button still says the wrong thing about the state). -->
        <button
          v-if="retryable"
          type="button"
          class="button-submit m-0 mt-5 bg-red-600 hover:bg-red-700"
          :disabled="pending"
          @click="onRetry"
        >
          Retry purge
        </button>
        <button
          type="button"
          class="button-cancel m-0 mt-5"
          :class="retryable ? 'ml-3' : ''"
          :disabled="pending"
          @click="onRefresh"
        >
          Refresh
        </button>
        <button
          type="button"
          class="button-cancel m-0 mt-5 ml-3"
          @click="openRecord"
        >
          View erasure record
        </button>
      </div>
    </div>

    <form v-else class="flex flex-col items-start" @submit.prevent="onSubmit">
      <p class="text-sm text-gray-500">
        This is irreversible. Verification results, uploaded lists and result
        files are deleted from the database and S3, stored cards are detached
        from Stripe, integration credentials are deleted, and the account
        identity is replaced with a tombstone address.
      </p>
      <p class="mt-2 text-sm text-gray-500">
        <span class="font-medium"
          >Every user in this organization is erased</span
        >, not just this one, so this has to be run against the parent account.
        Access is cut immediately; the data purge then runs in the background
        and can take a few minutes.
      </p>
      <p class="mt-2 text-sm text-gray-500">
        Kept: billing history, credit-pack records, Stripe charges and the audit
        trail.
      </p>

      <!-- Required, not optional: the reason is the only field on the audit row
           that says why the erasure was lawful, and the serializer rejects a
           blank one. -->
      <label for="erase-reason" class="mt-6">Reason</label>
      <div class="mt-1 w-full">
        <input
          id="erase-reason"
          type="text"
          v-model="reason"
          placeholder="e.g. GDPR SAR-17"
          class="form-field-no-icon sm:w-full outline-none"
          @input="formActions.clearFieldError('reason')"
        />
        <p v-show="errors.reason" class="text-red-500 text-sm mt-1">
          {{ errors.reason }}
        </p>
      </div>

      <label for="confirm-email" class="mt-4">
        Retype {{ user.email }} to confirm
      </label>
      <div class="mt-1 w-full">
        <input
          id="confirm-email"
          type="text"
          autocomplete="off"
          v-model="confirmEmail"
          :placeholder="user.email"
          class="form-field-no-icon sm:w-full outline-none"
          @input="formActions.clearFieldError('confirm_email')"
        />
        <p v-show="errors.confirm_email" class="text-red-500 text-sm mt-1">
          {{ errors.confirm_email }}
        </p>
      </div>

      <!-- user_id has no input of its own, but the serializer rejects on it:
           "User not found", "Staff accounts cannot be erased here", the
           sub-user rejection and the unfinished-request rejection. Without this
           block those would vanish silently - formMixin treats a 400 as handled
           once formActions is passed, so no toast fires either. -->
      <p v-show="errors.user_id" class="text-red-500 text-sm mt-3">
        {{ errors.user_id }}
      </p>

      <button
        type="submit"
        class="button-submit m-0 mt-5 bg-red-600 hover:bg-red-700"
        :disabled="!canSubmit"
      >
        Erase account
      </button>
    </form>
  </div>
</template>

<script>
import { ref } from "vue";
import eventBus from "@/utils/eventBus";
import formMixin from "@/mixins/formMixin";
import useFormErrors from "@/api/formErrors";
import { ERASURE_RECORD_EVENT } from "@/components/admin/user-page/modals/erasureEvents";

// Mirrors STALE_PURGE_AFTER in ak/core/account_deletion.py: the age at which the
// backend stops treating a claimed run as alive and lets it be re-dispatched.
// Kept in step by hand - a run the UI offers to retry any earlier just gets a
// "This erasure is already running" back.
const STALE_PURGE_AFTER_MS = 60 * 60 * 1000;

export default {
  name: "UserErasurePanel",
  mixins: [formMixin],
  props: {
    user: Object,
    // From the route, so a string. It is the only id here that is guaranteed to
    // be the account the admin actually opened.
    userId: [String, Number],
  },
  async beforeMount() {
    await this.fetchRecords();
  },
  setup() {
    const { errors, formActions } = useFormErrors();
    return {
      errors,
      formActions,
      confirmEmail: ref(""),
      reason: ref(""),
      pending: ref(false),
      latestRecord: ref(null),
    };
  },
  computed: {
    latestStatus() {
      return this.latestRecord ? this.latestRecord.status : null;
    },
    // The endpoint refuses to open a second request while one is unfinished, so
    // anything short of COMPLETED means the only thing on offer here is a retry.
    outstanding() {
      return Boolean(this.latestRecord) && this.latestStatus !== "COMPLETED";
    },
    // Mirrors what the retry endpoint will actually accept: a PARTIAL run is
    // resumable, and an IN_PROGRESS one only once it has gone stale (the
    // backend's STALE_PURGE_AFTER, an hour). PENDING is left out on purpose -
    // the task is queued and has not had its chance yet.
    retryable() {
      if (this.latestStatus === "PARTIAL") {
        return true;
      }
      if (
        this.latestStatus !== "IN_PROGRESS" ||
        !this.latestRecord.started_at
      ) {
        return false;
      }
      const startedAt = new Date(this.latestRecord.started_at).getTime();
      return Date.now() - startedAt > STALE_PURGE_AFTER_MS;
    },
    // The store's user outlives a single detail page, so between navigating to
    // another account and FETCH_USER landing it still holds the previous one.
    // Erasing takes both the target id and the email to confirm against off
    // that object, so a stale one agrees with itself and the server's
    // re-validation passes - against the wrong account. Only trust it once it
    // matches the id in the URL.
    isUserLoaded() {
      return Boolean(this.user && String(this.user.id) === String(this.userId));
    },
    canSubmit() {
      // Mirrors the serializer's checks so the button only arms once the form
      // would actually be accepted. The server re-validates regardless, so this
      // is a guard rail rather than the check itself.
      const email = (this.user && this.user.email) || "";
      return (
        !this.pending &&
        this.isUserLoaded &&
        // Without this an unloaded user - email undefined, so "" - would arm
        // the button against an empty input.
        email.length > 0 &&
        this.reason.trim().length > 0 &&
        this.confirmEmail.trim().toLowerCase() === email.toLowerCase()
      );
    },
  },
  methods: {
    formattedDate(timestamp) {
      return timestamp ? new Date(timestamp).toLocaleString("en-US") : null;
    },
    openRecord() {
      eventBus.trigger(ERASURE_RECORD_EVENT, {
        userId: this.userId,
        email: this.user.email,
      });
    },
    async fetchRecords() {
      // this.userId, not this.user.id: records are fetched on mount, which can
      // beat FETCH_USER, and the URL is the account the admin asked for.
      const response = await this.handleSubmissionErrors(
        () => this.$alfredService.adminGetUserErasures(this.userId),
        {}
      );
      this.latestRecord =
        (response && response.results && response.results[0]) || null;
    },
    // The Refresh button, unlike the mount-time fetch, has to re-read the user
    // too: once the purge completes, `data_purged_at` lands on the user rather
    // than on the record, and without it `outstanding` goes false while the
    // prop still looks un-erased - dropping the admin back onto the destructive
    // form for an account that is already gone.
    async onRefresh() {
      await Promise.all([
        this.fetchRecords(),
        this.$store.dispatch("user/FETCH_USER", { userId: this.userId }),
      ]);
    },
    async onSubmit() {
      if (!this.canSubmit) {
        return;
      }
      this.pending = true;
      // user_id errors have no input to clear them on - unlike confirm_email,
      // which clears on keystroke - so without this a "User not found" from a
      // failed attempt stays on screen through the retry that succeeds.
      this.formActions.clearFormErrors();
      try {
        // The id from the URL rather than the store's user. canSubmit has
        // already checked the two agree; this way the erasure target cannot
        // come from an object the store happens to be holding. DRF coerces the
        // string the route hands us.
        const response = await this.handleSubmissionErrors(
          () =>
            this.$alfredService.adminEraseUserData({
              user_id: this.userId,
              confirm_email: this.confirmEmail.trim(),
              reason: this.reason.trim(),
            }),
          { formActions: this.formActions }
        );
        if (!response) {
          // A 400 has already been rendered onto the fields; stay put.
          return;
        }
        // Deliberately not "erased": the 202 is the PENDING row. The purge runs
        // on a worker and sets data_purged_at when it finishes.
        this.notify("Account deactivated. Data purge is running.");
        this.confirmEmail = "";
        this.latestRecord = response;
        await this.$store.dispatch("user/FETCH_USER", { userId: this.userId });
      } finally {
        this.pending = false;
      }
    },
    async onRetry() {
      this.pending = true;
      this.formActions.clearFormErrors();
      try {
        // Retry takes the request id, not the user: a partial run has already
        // tombstoned the email, so there is nothing left for confirm_email to
        // match and the confirmation happened on the original request anyway.
        const response = await this.handleSubmissionErrors(
          () => this.$alfredService.adminRetryUserErasure(this.latestRecord.id),
          { formActions: this.formActions }
        );
        if (!response) {
          // "This erasure is already running" arrives as `detail`, which
          // formMixin has already raised as a toast.
          return;
        }
        this.notify("Purge re-dispatched.");
        this.latestRecord = response;
        await this.$store.dispatch("user/FETCH_USER", { userId: this.userId });
      } finally {
        this.pending = false;
      }
    },
    notify(text) {
      eventBus.trigger("notify", {
        notification: { group: "top-right", type: "success", text },
      });
    },
  },
};
</script>
